import * as Sentry from "@sentry/gatsby";
import { groupBy } from "lodash";
import { SagaIterator } from "redux-saga";
import { call, put, select } from "redux-saga/effects";

import { ScanTicketBulk, ScanTicketBulkMutation } from "@graphql/sdk";
import actions from "@state/actions";
import { getEventId, getLoggedIn, getOrgId } from "@state/selectors";
import { DeviceTicket } from "@state/types";
import * as api from "@util/api";
import { scanStorage, ticketStorage } from "@util/cache";
import { getTicketScans } from "@util/ticketScans";

type ScanTicketBulkWithError = ScanTicketBulk & { error?: boolean };

export default function* syncPendingScans(): SagaIterator {
  const loggedIn = yield select(getLoggedIn);

  if (!loggedIn) {
    console.log("Not logged in, no sync");
    return;
  }

  const pendingScans: ScanTicketBulkWithError[] = yield call(
    scanStorage!.values
  );

  if (pendingScans.length === 0) {
    console.log("No tickets to sync");
    yield put(actions.syncPendingScans.success());
    return;
  }

  const orgId = yield select(getOrgId);
  const eventId = yield select(getEventId);

  console.debug("Syncing scans:", pendingScans);
  yield put(actions.syncPendingScans.request());

  try {
    const result: ScanTicketBulkMutation = yield call(api.syncTickets, {
      scans: pendingScans,
      orgId,
    });
    if (result && result.scanTicketBulk) {
      // Mark unsuccessful scans with an error prop so we can filter them below
      result.scanTicketBulk.forEach((item, index: number) => {
        if (item.status?.toLowerCase() !== "ok") {
          console.error("bulk ticket Status not OK", item);
          pendingScans[index].error = true;
        }
      });

      // Returned history also covers retries that the server already accepted.
      const tickets = result.scanTicketBulk.flatMap((item, index) =>
        (pendingScans[index].eventId == null ||
          pendingScans[index].eventId === eventId) &&
        item.ticket
          ? [item.ticket]
          : []
      );
      const byTicketNumber = groupBy(tickets, "ticketNumber");
      for (const ticketNumber in byTicketNumber) {
        const ticket: DeviceTicket | undefined = yield call(
          ticketStorage!.get,
          ticketNumber
        );
        if (ticket) {
          const scans = [
            ...ticket.scans,
            ...byTicketNumber[ticketNumber].flatMap((item) =>
              (item.scans ?? []).map(({ id, scannedAt, direction, type }) => ({
                id,
                scannedAt,
                direction,
                type,
              }))
            ),
          ];
          // Finish the canonical ticket write before removing pending history.
          yield call(ticketStorage!.set, ticketNumber, {
            ...ticket,
            scans: getTicketScans({ ...ticket, scans }, [], eventId),
          });
        }
      }

      // Rejected offline scans are reported to Sentry rather than retried.
      const scanKeys = pendingScans.map((scan) => scan.scannedAt);
      yield call(scanStorage!.delMany, scanKeys);
      yield put(actions.updatePendingScanCount());
      yield put(actions.syncPendingScans.success());

      const errorScans = pendingScans
        .filter((scan) => scan.error)
        .map(({ error, ...scan }) => scan);
      if (errorScans.length > 0) {
        Sentry.captureException(new Error("Offline scan sync errors"), {
          extra: {
            scans: JSON.stringify(errorScans),
          },
        });
      }
    } else {
      yield put(actions.syncPendingScans.failure("nothing-burger"));
    }
  } catch (error) {
    yield put(actions.syncPendingScans.failure("nothing-burger"));
  }
}
